import {createSlice, createSelector, PayloadAction} from "@reduxjs/toolkit";
import {AppThunk, RootState} from "../../app/store";
import {LayoutModel,
        LayoutFactory,
        ObjectModel,
        ObjectFactory,
        FileModel,
        RecordModel,
        RecordFactory,
        ActionModel,
        ActionFactory} from "../../models/models";
import Config from "../../config";
import {storeCelebrations} from "../celebrations/celebrationsSlice";

interface RecordState {
  isFetching: boolean;
  record: RecordModel | null;
  objectMetadata: ObjectModel | null;
  selectedLayout: LayoutModel | null;
  layouts: LayoutModel[];
  actions: ActionModel[];
  error: string | null;
}

const initialState: RecordState = {
  isFetching: false,
  record: null,
  objectMetadata: null,
  selectedLayout: null,
  layouts: [],
  actions: [],
  error: null
};

export const recordSlice = createSlice({
  name: "record",
  initialState,
  reducers: {
    storeFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },
    storeRecord: (state, action: PayloadAction<RecordModel | null>) => {
      state.record = action.payload;
    },
    storeSelectedLayout: (state, action: PayloadAction<LayoutModel | null>) => {
      state.selectedLayout = action.payload;
    },
    storeLayouts: (state, action: PayloadAction<LayoutModel[]>) => {
      state.layouts = action.payload.map((record) => record);
    },
    storeActions: (state, action: PayloadAction<ActionModel[]>) => {
      state.actions = action.payload.map((record) => record);
    },
    storeObjectMetadata: (state, action: PayloadAction<ObjectModel | null>) => {
      state.objectMetadata = action.payload;
    },
    storeError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    purgeStore: (state) => {
//      state.objectMetadata = null;
      state.record = null;
      state.selectedLayout = null;
      state.layouts = [];
      state.actions = [];
      state.isFetching = false;
      state.error = null;
    },
  },
});



export const { storeRecord, storeObjectMetadata, storeSelectedLayout, storeLayouts, storeActions, storeFetching, storeError, purgeStore } = recordSlice.actions;


export const retrieveRecord =
  (objectApiName: string | null,
   recordUuid: string | null,
   layoutApiName: string | null,
   accessToken: string,
   refetch: boolean,
   handleInvalidToken: () => void): AppThunk =>
  async (dispatch) => {

    //load listviews, selected list view, records and object metadata
    //for a given objectApiName
    //if refetch is true, object metadata state is not being altered

    let url = `${Config.getInstance().getCoordinationServiceURL()}/api/r/${objectApiName}/${recordUuid}`;

    const headers = new Headers();

    headers.append("Authorization", `Bearer ${accessToken}`);
    headers.append("Accept", "application/json");
    headers.append("Content-Type", "application/json");
    headers.append("crm-layout-api-name", `${layoutApiName}`);

    try {

      dispatch(purgeStore());
      dispatch(storeFetching(true));

      const response = await fetch(url, {
        method: "GET",
        headers: headers
      })

      if(response.status===401){
        handleInvalidToken();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        if(!errorData.message){
          dispatch(storeError("Error when fetching records"));
        } else{
          dispatch(storeError(errorData.message));
        }
        dispatch(storeFetching(false));
        return;
      } else {
        dispatch(storeError(null));
      }

      const data = await response.json();

      if(!data){
          dispatch(storeError("Error when fetching data"));
          dispatch(storeFetching(false));
          return;
      }

      if(!data.record){
          dispatch(storeError("Error when fetching data"));
          dispatch(storeFetching(false));
          return;
      }

      if(!data.metadata || !data.metadata.object){
          dispatch(storeError("Error when fetching data"));
          dispatch(storeFetching(false));
          return;
      }

      if(!data.metadata || !data.metadata.layout){
          dispatch(storeError("Error when fetching data"));
          dispatch(storeFetching(false));
          return;
      }

      if(!data.metadata || !data.metadata.layouts || !Array.isArray(data.metadata.layouts)){
          dispatch(storeError("Error when fetching layouts"));
          dispatch(storeFetching(false));
          return;
      }

      if(!data.metadata || !data.metadata.actions || !Array.isArray(data.metadata.actions)){
          dispatch(storeError("Error when fetching layouts"));
          dispatch(storeFetching(false));
          return;
      }

      //store object metadata
      dispatch(
        storeObjectMetadata(new ObjectFactory().fromJSON(data.metadata.object))
      )

      //store layout metadata
      dispatch(
        storeSelectedLayout(new LayoutFactory().fromJSON(data.metadata.layout))
      )

      //store layouts
      dispatch(
        storeLayouts(data.metadata.layouts.map((layout: any) => new LayoutFactory().fromJSON(layout)))
      )

      //store layouts
      dispatch(
        storeActions(data.metadata.actions.map((action: any) => new ActionFactory().fromJSON(action)))
      )

      //store record
      dispatch(
        storeRecord(new RecordFactory().fromJSON(data.record))
      )

      if(data.celebrations){
        dispatch(storeCelebrations(data.celebrations));
      }


      dispatch(storeFetching(false));

    } catch (error) {
      const errorMessage = "Unhandled error occurred";
      dispatch(storeError(errorMessage));
      dispatch(storeFetching(false));
      console.error(error);
    }
  };


export const updateRecord =
    (objectApiName: string | null,
     recordUuid: string | null,
     updatedRecord: RecordModel,
     authToken: string): AppThunk =>
  async (dispatch) => {

    let url = `${Config.getInstance().getCoordinationServiceURL()}/api/u/${objectApiName}/${recordUuid}`;

    const headers = new Headers();
    const requestBody = new RecordFactory().toJSON(updatedRecord);

    const all_files = new RecordFactory().getFILES(updatedRecord);

    const requestBlob = new Blob([JSON.stringify(requestBody)], {type: "application/json"});
    const formData = new FormData();
    formData.append('json', requestBlob, 'record.json')

    all_files.map((thisFile: FileModel) => {
      formData.append(thisFile.fieldName, thisFile.file)
    })

    headers.append("Authorization", `Bearer ${authToken}`);

    try {

      dispatch(storeFetching(true));

      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: formData
      })

      if (!response.ok) {
        const errorData = await response.json();
        let errorMessage = ""
        if(!errorData.message){
          errorMessage = "Error when updating record"
        } else{
          errorMessage = errorData.message;
        }
        dispatch(storeFetching(false));
        return errorMessage;
      } else {
        dispatch(storeError(null));
      }

      const data = await response.json();

      if(!data.record){
        const errorMessage = "Error when updating record"
        dispatch(storeFetching(false));
        return errorMessage;
      }

      //store record
      dispatch(
        storeRecord(new RecordFactory().fromJSON(data.record))
      )

      //store celebrations
      if(data.celebrations){
        dispatch(storeCelebrations(data.celebrations));
      }

      dispatch(storeFetching(false));

      return 'success';

    } catch (error) {
      const errorMessage = "Unhandled error occurred";
      dispatch(storeFetching(false));
      return errorMessage
    }
  };

export const deleteRecord =
    (objectApiName: string | null,
     recordUuid: string | null,
     authToken: string): AppThunk =>
  async (dispatch) => {

    let url = `${Config.getInstance().getCoordinationServiceURL()}/api/d/${objectApiName}/${recordUuid}`;

    const headers = new Headers();
    const requestBody = {};

    headers.append("Authorization", `Bearer ${authToken}`);
    headers.append("Accept", "application/json");
    headers.append("Content-Type", "application/json");

    try {
      dispatch(storeFetching(true));

      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const errorData = await response.json();
        let errorMessage = ""
        if(!errorData.message){
          errorMessage = "Error when deleting record"
        } else{
          errorMessage = errorData.message;
        }
        dispatch(storeFetching(false));
        return errorMessage;
      } else {
        dispatch(storeError(null));
      }

      const data = await response.json();
/*
      if(!data.record){
        const errorMessage = "Error when updating record"
        dispatch(storeFetching(false));
        return errorMessage;
      }
*/

      dispatch(storeFetching(false));

      dispatch(purgeStore())

      return 'success';

    } catch (error) {
      const errorMessage = "Unhandled error occurred";
      dispatch(storeFetching(false));
      return errorMessage
    }
  };


export const downloadFile =
    (objectApiName: string | null,
     recordUuid: string | null,
     fieldApiName: string | null,
     authToken: string): AppThunk =>
  async (dispatch) => {

    let url = `${Config.getInstance().getCoordinationServiceURL()}/api/rd/${objectApiName}/${recordUuid}/${fieldApiName}`;

    const headers = new Headers();

    headers.append("Authorization", `Bearer ${authToken}`);
    headers.append("Accept", "application/json");
    headers.append("Content-Type", "application/json");

    try {
      dispatch(storeFetching(true));

      const response = await fetch(url, {
        method: "GET",
        headers: headers
      })

      if (!response.ok) {
        const errorData = await response.json();
        let errorMessage = ""
        if(!errorData.message){
          errorMessage = "Error when deleting record"
        } else{
          errorMessage = errorData.message;
        }
        dispatch(storeFetching(false));
        console.log(errorMessage)
        return errorMessage;
      } else {
        dispatch(storeError(null));
      }

      const filename = response.headers.get('crm-filename') || 'my_file';
      const this_blob = await response.blob();
      const downloadURL = window.URL.createObjectURL(this_blob);
      let a = document.createElement('a');
      a.href = downloadURL;
      a.download = filename;
      a.click();

      dispatch(storeFetching(false));

      return 'success';

    } catch (error) {
      const errorMessage = "Unhandled error occurred";
      dispatch(storeFetching(false));
      return errorMessage
    }
  };

export const selectIsFetching = (state: RootState) =>
  state.recordStore.isFetching;

export const selectError = (state: RootState) =>
  state.recordStore.error;

export const selectObjectMetadata = (state: RootState) =>{
    return state.recordStore.objectMetadata;
}

export const selectSelectedLayout = (state: RootState) =>{
    return state.recordStore.selectedLayout;
}

export const selectLayouts = (state: RootState) =>{
    return state.recordStore.layouts;
}

/*
NOTE: this gives warnng:
Selector foo returned a different result when called with the same parameter;
Selectors that return a new reference (such as an object or an array) should be memoized.
-> Thus, use selector below
export const selectActions = (state: RootState) =>{
    return state.recordStore.actions.filter((thisAction) => {
                            if(thisAction.OnSuccess_sys.type ==='dialog' ||
                               thisAction.OnSuccess_sys.type ==='snackbar' ||
                               thisAction.OnSuccess_sys.type ==='flow' ||
                               thisAction.Type_sys === 'api-link') return true;
                            return false
    })
}
*/
export const selectActions = createSelector(
  [
    (state: RootState) => state.recordStore.actions
  ],
  (actions) => {
    //filter actions
    return actions.filter((thisAction) => {
                        if(thisAction.OnSuccess_sys.type ==='dialog' ||
                           thisAction.OnSuccess_sys.type ==='snackbar' ||
                           thisAction.OnSuccess_sys.type ==='flow' ||
                           thisAction.OnSuccess_sys.type ==='download' ||
                           thisAction.Type_sys === 'api-link') return true;
                        return false
    })
  }
)

export const selectRecord = (state: RootState) =>{
    return state.recordStore.record;
}

export default recordSlice.reducer;
